/**
 * Re-encode an uploaded image at a size a story page actually displays.
 *
 * Story images are posted to the backend as base64 data URLs inside a JSON
 * body — roughly 1.37x inflation — and land in the frozen story that every
 * reader downloads. A modern phone photo is several times larger than anything
 * the layout can show, so it is resized and re-encoded here before upload:
 * uploads stop failing on the request-size limit, and published stories stay
 * small for readers.
 */

/** Longest edge kept, in CSS pixels. Comfortably above the widest render slot. */
export const MAX_IMAGE_EDGE_PX = 2048;

/** JPEG/WebP quality for re-encoded photos. */
const RE_ENCODE_QUALITY = 0.85;

/** Above this, an image is re-encoded even when its dimensions are already fine. */
const RE_ENCODE_ABOVE_BYTES = 1.5 * 1024 * 1024;

/** Formats worth re-encoding as-is; PNG stays PNG so flat graphics don't get artefacts. */
const RE_ENCODE_MIME: Record<string, string> = {
  "image/jpeg": "image/jpeg",
  "image/webp": "image/webp",
  "image/png": "image/png",
};

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not decode the image"));
    };
    img.src = url;
  });
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error("Could not read the file"));
    reader.onerror = () => reject(new Error("Could not read the file"));
    reader.readAsDataURL(blob);
  });
}

/**
 * Return a data URL for `file`, downscaled and re-encoded when that helps.
 *
 * Falls back to the original bytes whenever anything about the canvas path
 * fails (a decode failure, a tainted canvas, an unsupported type) — the server
 * still enforces its own limits, so the worst case is a larger upload rather
 * than a broken one.
 */
export async function toUploadableDataUrl(file: File): Promise<string> {
  const outputMime = RE_ENCODE_MIME[file.type];
  if (!outputMime) return readAsDataUrl(file);

  try {
    const img = await loadImage(file);
    const longestEdge = Math.max(img.naturalWidth, img.naturalHeight);
    const needsResize = longestEdge > MAX_IMAGE_EDGE_PX;
    if (!needsResize && file.size <= RE_ENCODE_ABOVE_BYTES) {
      return readAsDataUrl(file);
    }

    const scale = needsResize ? MAX_IMAGE_EDGE_PX / longestEdge : 1;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return readAsDataUrl(file);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL(outputMime, RE_ENCODE_QUALITY);
    // A re-encode can come out larger (small PNGs especially) — keep whichever
    // is smaller rather than assuming the canvas always wins.
    const original = await readAsDataUrl(file);
    return dataUrl.length < original.length ? dataUrl : original;
  } catch {
    return readAsDataUrl(file);
  }
}

/** Approximate decoded byte count of a base64 data URL (what the server measures). */
export function dataUrlByteLength(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}
